import { useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  FormControlLabel,
  Checkbox,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/navbar";

const IIITHCourseForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    college: "",
    year: "",
    branch: "",
    whatsapp: "",
    email: "",
    city: "",
    aadhar: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    college: "",
    year: "",
    branch: "",
    whatsapp: "",
    email: "",
    city: "",
    aadhar: "",
  });

  const [formError, setFormError] = useState("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const termsRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 5) {
      setHasScrolledToBottom(true);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      college: "",
      year: "",
      branch: "",
      whatsapp: "",
      email: "",
      city: "",
      aadhar: "",
    };

    // Name
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    // College
    if (!formData.college.trim()) {
      newErrors.college = "College name is required";
      isValid = false;
    }

    // Year
    if (!formData.year.trim()) {
      newErrors.year = "Year is required";
      isValid = false;
    }

    // Branch
    if (!formData.branch.trim()) {
      newErrors.branch = "Branch is required";
      isValid = false;
    }

    // WhatsApp
    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = "WhatsApp number is required";
      isValid = false;
    } else if (!/^\+?[0-9\s\-()]{8,20}$/.test(formData.whatsapp)) {
      newErrors.whatsapp = "Please enter a valid WhatsApp number";
      isValid = false;
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // City
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
      isValid = false;
    }

    // Aadhaar
    if (!formData.aadhar.trim()) {
      newErrors.aadhar = "Aadhaar number is required";
      isValid = false;
    } else if (!/^[0-9]{12}$/.test(formData.aadhar)) {
      newErrors.aadhar = "Please enter a valid 12-digit Aadhaar number";
      isValid = false;
    }

    // Terms
    if (!hasAcceptedTerms) {
      setFormError("You must accept the Terms and Conditions to proceed");
      isValid = false;
    } else if (!hasScrolledToBottom) {
      setFormError("Please read the Terms and Conditions completely");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    if (validateForm()) {
      try {
        navigate("/iiithcourseselct", { state: { formData } });
      } catch (error) {
        setFormError("An error occurred. Please try again.");
      }
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#f9f9f9",
        minHeight: "100vh",
        backgroundImage: "linear-gradient(to bottom right, #f9f9f9, #f2f2f2)",
      }}
    >
      <Navbar />

      <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
        <Box sx={{ mb: 5, textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Aclonica",
              fontSize: { xs: "36px", md: "48px" },
              fontWeight: 700,
              mb: 2,
              background: "linear-gradient(45deg, #333, #666)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Apply for IIITH_prep
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: "600px", mx: "auto" }}
          >
            Complete the form below to register. All fields are required.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            bgcolor: "white",
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            maxWidth: "900px",
            mx: "auto",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "6px",
              background: "linear-gradient(to right,#30B0C7,rgb(49, 195, 221))",
            },
          }}
        >
          {formError && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
              {formError}
            </Alert>
          )}

          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 550,
              fontFamily: "Aclonica",
              color: "#333",
            }}
          >
            Personal Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="College Name"
                name="college"
                value={formData.college}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.college)}
                helperText={errors.college}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                required
                fullWidth
                label="Year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.year)}
                helperText={errors.year}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                required
                fullWidth
                label="Branch"
                name="branch"
                value={formData.branch}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.branch)}
                helperText={errors.branch}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="WhatsApp Number"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.whatsapp)}
                helperText={errors.whatsapp}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.city)}
                helperText={errors.city}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Aadhaar Number"
                name="aadhar"
                value={formData.aadhar}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.aadhar)}
                helperText={errors.aadhar}
              />
            </Grid>

            {/* Terms and Conditions */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mt: 2,
                  mb: 2,
                  fontWeight: 550,
                  fontFamily: "Aclonica",
                  color: "#333",
                }}
              >
                Terms and Conditions
              </Typography>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  p: 2,
                  borderRadius: 2,
                  borderColor: "#ddd",
                  bgcolor: "#fafafa",
                }}
                ref={termsRef}
                onScroll={handleTermsScroll}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  IIITH_prep Terms and Conditions
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Effective Date:</strong> 22/09/2025
                </Typography>
                <Typography variant="body2" paragraph>
                  By registering with <b>IIITH_prep</b>, you agree to abide by
                  the following terms and conditions. Please read carefully
                  before proceeding.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold">
                  1. Registration & Fees
                </Typography>
                <Typography variant="body2" paragraph>
                  - Access credentials are non-transferable. <br />- All
                  payments must be completed before access is granted.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold">
                  2. Services
                </Typography>
                <Typography variant="body2" paragraph>
                  - Test series and interview sessions are conducted online.{" "}
                  <br />- IIITH_prep reserves the right to reschedule sessions
                  when necessary.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold">
                  3. User Responsibilities
                </Typography>
                <Typography variant="body2" paragraph>
                  - Ensure stable internet during tests/sessions. <br />
                  - Follow code of conduct during all sessions. <br />- Misuse
                  of content (copying, sharing, recording) is strictly
                  prohibited.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold">
                  4. Privacy
                </Typography>
                <Typography variant="body2" paragraph>
                  - Personal data such as name, email, Aadhaar, and contact
                  details will be used only for registration and communication
                  purposes. <br />- IIITH_prep will not share your data with
                  third parties without consent.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold">
                  5. Liability
                </Typography>
                <Typography variant="body2" paragraph>
                  - IIITH_prep is not responsible for issues arising due to
                  user-side technical failures. <br />- The final decision
                  regarding admissions/prep services rests solely with
                  IIITH_prep.
                </Typography>
              </Paper>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasAcceptedTerms}
                      onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                      disabled={!hasScrolledToBottom}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and agree to the Terms and Conditions
                      {!hasScrolledToBottom && (
                        <Typography
                          component="span"
                          color="error"
                          sx={{ ml: 1 }}
                        >
                          (Please Read the entire document)
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ mt: 3, textAlign: "center" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={!hasAcceptedTerms}
                sx={{
                  mt: 2,
                  py: 1.5,
                  px: 5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: "#30B0C7",
                  "&:hover": {
                    backgroundColor: "rgb(42, 156, 177)",
                  },
                }}
              >
                Submit Application
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default IIITHCourseForm;
